import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { useToast } from '@/hooks/use-toast';
import { DEBUG_LIVE_RESHUFFLE } from '@/constants/schedulingConstants';
// DTC-F: route the realtime-stability + reshuffle-debounce "now" anchors
// through the canonical clock so backoff and debounce logic are
// deterministic under `clock.setFixed(...)`. The 10s stability window
// and 10s debounce window remain unchanged in semantics.
import { clock } from '@/utils/clock';

const DEBOUNCE_MS = 10000; // Max 1 reshuffle per 10 seconds

interface LiveReshuffleOptions {
  shopId: string | null;
  selectedDayLabel: string;
  enabled?: boolean;
}

export const useLiveReshuffle = ({ shopId, selectedDayLabel, enabled = true }: LiveReshuffleOptions) => {
  const { toast } = useToast();
  const lastReshuffleRef = useRef<number>(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const hasShownErrorRef = useRef<boolean>(false);
  // Track consecutive CHANNEL_ERROR / TIMED_OUT events so a single
  // transient blip (e.g. token-rotation reconnect handled invisibly by
  // backoff) doesn't surface a toast. Reset on SUBSCRIBED.
  const consecutiveFailuresRef = useRef<number>(0);
  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountingRef = useRef<boolean>(false);
  const lastSubscribedAtRef = useRef<number>(0);
  // Keep toast in a ref so it's not in the dependency array
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    if (!enabled || !shopId) {
      if (DEBUG_LIVE_RESHUFFLE) {
        console.log('[LiveReshuffle] Disabled or no shop ID');
      }
      return;
    }

    isUnmountingRef.current = false;
    const dateStr = selectedDayLabel;

    // Reconnection constants
    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY_MS = 1000;

    const createAndSubscribeChannel = () => {
      const channel = supabase
        .channel(`schedule-changes-${shopId}-${dateStr}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'tickets',
            filter: `scheduled_date=eq.${dateStr}`,
          },
          (payload) => {
            requestAnimationFrame(() => {
              if (payload.new?.shop_id && payload.new.shop_id !== shopId) return;
              if (payload.new?.parking_lot === true || payload.old?.parking_lot === true) return;

              // Chronomancer R-WAITER-7: include Waiter-contract fields in
              // the reshuffle trigger set. `scheduled_start_time` and `type`
              // are the canonical anchors for Waiter promise semantics —
              // changing either flips the ticket between "locked
              // appointment" and "flexible slot" and MUST re-cascade. Prior
              // list missed both, so:
              //   - A Waiter → Drop-off conversion (EditTicketDetailsModal,
              //     DueOutMitigationModal, EODRolloverModal) cleared the
              //     lock but left every downstream ticket at their old
              //     placements until some other change nudged cascade.
              //   - A Waiter re-booking that updated scheduled_start_time
              //     without touching start_time/opti_finish had the same
              //     effect — customers saw the updated appointment time
              //     but the schedule around them didn't reflow.
              const schedulingFields = [
                'assigned_tech_id',
                'start_time',
                'opti_finish',
                'status',
                'predicted_minutes',
                'parking_lot',
                'scheduled_start_time',
                'type',
              ];
              const hasSchedulingChange = schedulingFields.some(
                field => payload.new?.[field] !== payload.old?.[field]
              );
              if (!hasSchedulingChange) return;

              if (DEBUG_LIVE_RESHUFFLE) {
                console.log('[LiveReshuffle] Ticket UPDATE:', payload);
              }
              triggerReshuffle('tickets:UPDATE', payload);
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'tickets',
            filter: `scheduled_date=eq.${dateStr}`,
          },
          (payload) => {
            if (payload.new?.shop_id && payload.new.shop_id !== shopId) return;
            if (payload.new?.parking_lot === true) return;
            if (DEBUG_LIVE_RESHUFFLE) {
              console.log('[LiveReshuffle] Ticket INSERT:', payload);
            }
            triggerReshuffle('tickets:INSERT', payload);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'tickets',
            filter: `scheduled_date=eq.${dateStr}`,
          },
          (payload) => {
            if (payload.old?.shop_id && payload.old.shop_id !== shopId) return;
            if (DEBUG_LIVE_RESHUFFLE) {
              console.log('[LiveReshuffle] Ticket DELETE:', payload);
            }
            triggerReshuffle('tickets:DELETE', payload);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'technicians',
            filter: `shop_id=eq.${shopId}`,
          },
          (payload) => {
            if (DEBUG_LIVE_RESHUFFLE) {
              console.log('[LiveReshuffle] Technician UPDATE:', payload);
            }
            triggerReshuffle('technicians:UPDATE', payload);
          }
        )
        .subscribe((status) => {
          if (DEBUG_LIVE_RESHUFFLE) {
            console.log('[LiveReshuffle] Channel status:', status);
          }

          if (status === 'SUBSCRIBED') {
            console.log('[LiveReshuffle] Successfully subscribed to realtime events');
            const now = clock.nowMs();
            const stableDuration = now - lastSubscribedAtRef.current;
            // Only reset backoff if first subscribe or previous connection lasted > 10s
            if (lastSubscribedAtRef.current === 0 || stableDuration > 10000) {
              reconnectAttemptRef.current = 0;
            }
            lastSubscribedAtRef.current = now;
            hasShownErrorRef.current = false;
            consecutiveFailuresRef.current = 0;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.error(`[LiveReshuffle] Channel ${status} - scheduling reconnect`);
            consecutiveFailuresRef.current += 1;
            // Skip the first transient: single CHANNEL_ERROR / TIMED_OUT
            // events are usually token-rotation reconnects that the
            // backoff path heals invisibly within seconds. Only surface
            // a calm toast (default variant, not destructive) once a
            // second consecutive failure suggests the backoff isn't
            // catching up.
            if (consecutiveFailuresRef.current >= 2 && !hasShownErrorRef.current) {
              hasShownErrorRef.current = true;
              toastRef.current({
                title: 'Reconnecting live updates',
                description: 'Schedule updates paused briefly. Reconnecting automatically...',
                skipPersist: true,
              });
            }
            scheduleReconnect();
          } else if (status === 'CLOSED') {
            // Only treat CLOSED as failure if we're NOT intentionally unmounting
            if (!isUnmountingRef.current) {
              console.error('[LiveReshuffle] Channel CLOSED unexpectedly - scheduling reconnect');
              scheduleReconnect();
            }
            // If intentional cleanup, do nothing
          }
        });

      return channel;
    };

    const scheduleReconnect = () => {
      if (isUnmountingRef.current) return;
      if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error('[LiveReshuffle] Max reconnection attempts reached');
        return;
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      const delay = BASE_DELAY_MS * Math.pow(2, reconnectAttemptRef.current);
      reconnectAttemptRef.current += 1;

      if (DEBUG_LIVE_RESHUFFLE) {
        console.log(`[LiveReshuffle] Scheduling reconnect attempt ${reconnectAttemptRef.current} in ${delay}ms`);
      }

      reconnectTimerRef.current = setTimeout(() => {
        if (isUnmountingRef.current) return;
        
        // Remove old channel and create a new one in-place (no state change, no effect re-trigger)
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
        }
        channelRef.current = createAndSubscribeChannel();
      }, delay);
    };

    const triggerReshuffle = async (source: string, payload?: any) => {
      const now = clock.nowMs();
      const timeSinceLastReshuffle = now - lastReshuffleRef.current;

      if (DEBUG_LIVE_RESHUFFLE) {
        console.log(`[LiveReshuffle] Event from ${source}:`, payload);
        console.log(`[LiveReshuffle] Time since last reshuffle: ${timeSinceLastReshuffle}ms`);
      }

      if (timeSinceLastReshuffle < DEBOUNCE_MS) {
        if (DEBUG_LIVE_RESHUFFLE) {
          console.log('[LiveReshuffle] Debouncing - scheduling reshuffle');
        }

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        const remainingTime = DEBOUNCE_MS - timeSinceLastReshuffle;
        debounceTimerRef.current = setTimeout(() => {
          triggerReshuffle(source + ' (debounced)', payload);
        }, remainingTime);

        return;
      }

      lastReshuffleRef.current = now;

      if (DEBUG_LIVE_RESHUFFLE) {
        console.log(`[LiveReshuffle] Triggering recalculation from ${source}`);
      }

      try {
        const { data: technicians, error: techError } = await supabase
          .from('technicians')
          .select('id')
          .eq('shop_id', shopId)
          .eq('employment_status', 'Active');

        if (techError) throw techError;

        if (!technicians || technicians.length === 0) {
          if (DEBUG_LIVE_RESHUFFLE) {
            console.log('[LiveReshuffle] No technicians found');
          }
          return;
        }

        // Delegate to server-side cascade (single authority for downstream chaining)
        if (shopId) {
          await triggerBoardCascade(shopId, dateStr);
        }

        if (DEBUG_LIVE_RESHUFFLE) {
          console.log(`[LiveReshuffle] Triggered board cascade for ${dateStr}`);
        }
      } catch (error) {
        console.error('[LiveReshuffle] Error during reshuffle:', error);
        toastRef.current({
          title: 'Schedule Update Error',
          description: 'Failed to recalculate schedules after changes.',
          variant: 'destructive',
          skipPersist: true,
        });
      }
    };

    // Create initial channel
    channelRef.current = createAndSubscribeChannel();

    return () => {
      if (DEBUG_LIVE_RESHUFFLE) {
        console.log('[LiveReshuffle] Cleaning up realtime subscriptions');
      }

      // Mark as intentional cleanup BEFORE removing channel
      isUnmountingRef.current = true;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      lastSubscribedAtRef.current = 0;
      // Do NOT reset hasShownErrorRef here — only reset on SUBSCRIBED
    };
  }, [shopId, selectedDayLabel, enabled]);
};
